import React, { useEffect, useRef, useState } from 'react';
import { Music, ArrowLeft, Guitar, Mic, Drum, Music2, Disc3, Piano } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Fallback map: production DB has 3 members with stale image URLs pointing
// at a decommissioned Emergent host (retro-band-cms.emergent.host → 404).
// The correct images are shipped in the repo at /images/band-members/*.jpg.
// Keyed by lowercase, trimmed member name.
const LOCAL_MEMBER_IMAGES = {
  'ben lamb': '/images/band-members/ben-lamb-lead-vocals.jpg',
  'keith lamb': '/images/band-members/keith-lamb-guitar.jpg',
  'rod cahoon': '/images/band-members/rod-cahoon-bass.jpg',
  'cameron clark': '/images/band-members/cam-clark-keyboards.jpg',
  'cam clark': '/images/band-members/cam-clark-keyboards.jpg',
  'mitch rasmussen': '/images/band-members/mitch-rasmussen-drums.jpg',
};

function resolveMemberImage(member) {
  const original = member && member.image ? String(member.image) : '';
  const nameKey = (member && member.name ? String(member.name) : '').trim().toLowerCase();
  // If the stored image points at a decommissioned host, prefer the local
  // file up front rather than waiting for the 404 → onError round-trip.
  if (/retro-band-cms\.emergent\.host/i.test(original) && LOCAL_MEMBER_IMAGES[nameKey]) {
    return LOCAL_MEMBER_IMAGES[nameKey];
  }
  return original;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BandMembers = () => {
  const navigate = useNavigate();
  const cardsRef = useRef([]);
  const [bandMembers, setBandMembers] = useState([]);

  useEffect(() => {
    fetchBandMembers();
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    // Remove the static homepage-oriented SEO tags so this page's Helmet tags
    // are the only ones for canonical/OG/Twitter/description. On unmount they
    // are already re-added on next full page load from public/index.html.
    const defaults = document.querySelectorAll('head [data-default="1"]');
    const removed = Array.from(defaults);
    removed.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    return () => {
      // Restore on unmount (SPA navigation back to /) so homepage retains its
      // baseline tags before Home.jsx / Helmet re-render pass completes.
      removed.forEach((el) => document.head.appendChild(el));
    };
  }, []);

  useEffect(() => {
    // Add scroll animation for cards
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('card-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [bandMembers]);

  const fetchBandMembers = async () => {
    try {
      const response = await axios.get(`${API}/band-members`);
      setBandMembers(response.data);
    } catch (error) {
      console.error('Error fetching band members:', error);
      setBandMembers([]);
    }
  };

  const getInstrumentIcon = (instrument) => {
    const instrumentLower = instrument.toLowerCase();
    
    // Check for drums first (including drums/vocals)
    if (instrumentLower.includes('drum')) {
      return <Drum className="instrument-icon" />;
    } 
    // Check for keyboard/piano/keys (including keys/vocals)
    else if (instrumentLower.includes('key') || instrumentLower.includes('piano') || instrumentLower.includes('keyboard')) {
      return <Piano className="instrument-icon" />;
    }
    // Check for guitar and bass (both use guitar icon since no separate bass icon exists)
    else if (instrumentLower.includes('guitar') || instrumentLower.includes('bass')) {
      return <Guitar className="instrument-icon" />;
    } 
    // Check for vocals only (not combined with instruments)
    else if ((instrumentLower.includes('vocal') || instrumentLower.includes('singer')) && 
             !instrumentLower.includes('/') && !instrumentLower.includes('&')) {
      return <Mic className="instrument-icon" />;
    } 
    else {
      return <Music2 className="instrument-icon" />;
    }
  };

  return (
    <div className="band-members-container">
      <Helmet>
        <title>Meet the Rebel Angels — Band Members | Lethbridge 50s Cover Band</title>
        <meta name="description" content="Meet the members of Rebel Angels, Lethbridge's premier 50s cover band: Ben Lamb, Keith Lamb, Rod Cahoon, Gary Drayton, and Mitch Rasmussen. Learn each musician's story and favorite artists." />
        <link rel="canonical" href="https://www.rebelangels.ca/band-members" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rebelangels.ca/band-members" />
        <meta property="og:title" content="Meet the Rebel Angels — Band Members | Lethbridge 50s Cover Band" />
        <meta property="og:description" content="Meet the members of Rebel Angels, Lethbridge's premier 50s cover band." />
        <meta property="og:image" content="https://www.rebelangels.ca/api/images/gallery/rebel-angels-live-performance-1.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.rebelangels.ca/band-members" />
        <meta name="twitter:title" content="Meet the Rebel Angels — Band Members" />
        <meta name="twitter:description" content="Meet the members of Rebel Angels, Lethbridge's premier 50s cover band." />
        <meta name="twitter:image" content="https://www.rebelangels.ca/api/images/gallery/rebel-angels-live-performance-1.jpg" />
      </Helmet>
      {/* Animated vinyl records background */}
      <div className="vinyl-background">
        <Disc3 className="vinyl-disc vinyl-1" />
        <Disc3 className="vinyl-disc vinyl-2" />
        <Disc3 className="vinyl-disc vinyl-3" />
      </div>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo-section" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_lethbridge-angels/artifacts/znfka10j_RA-blacktransparent%20cropped%20no%20vol.png" 
              alt="Rebel Angels Logo" 
              className="brand-logo"
            />
          </div>
          <nav className="nav-menu">
            <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); navigate('/'); }}>Home</a>
            <a href="#" className="nav-link" onClick={(e) => { 
              e.preventDefault(); 
              navigate('/');
              setTimeout(() => {
                const section = document.getElementById('about');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>About</a>
            <a href="#" className="nav-link" onClick={(e) => { 
              e.preventDefault(); 
              navigate('/');
              setTimeout(() => {
                const section = document.getElementById('shows');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>Shows</a>
            <a href="#" className="nav-link" onClick={(e) => { 
              e.preventDefault(); 
              navigate('/');
              setTimeout(() => {
                const section = document.getElementById('gallery');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>Gallery</a>
            <a href="#" className="nav-link" onClick={(e) => { 
              e.preventDefault(); 
              navigate('/');
              setTimeout(() => {
                const section = document.getElementById('contact');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>Book Us</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="members-hero-section">
        <div className="hero-content">
          <h1 className="hero-title animate-slide-down">Meet the Band</h1>
          <p className="hero-subtitle animate-slide-up">The Rebels Behind the Music</p>
          <Button 
            variant="outline" 
            className="back-button-hero"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="back-icon" />
            Back to Home
          </Button>
        </div>
      </section>

      {/* Band Members Section */}
      <section className="members-section">
        <div className="section-container">
          <h2 className="section-title">Local Legends</h2>
          <p className="members-intro">
            Meet the talented musicians who bring the 1950s back to life with every performance.
            Each member brings their unique style and passion for rock and roll to create an authentic
            50s experience.
          </p>
          <div className="members-grid">
            {bandMembers.map((member, index) => (
              <Card 
                key={member.id} 
                className="member-card card-animate"
                ref={(el) => (cardsRef.current[index] = el)}
              >
                <CardContent className="member-card-content">
                  <div className="member-image-container">
                    {(() => {
                      const resolvedSrc = resolveMemberImage(member);
                      return resolvedSrc ? (
                      <img 
                        src={resolvedSrc} 
                        alt={`${member.name} - ${member.instrument} - Rebel Angels 50s Cover Band Southern Alberta`}
                        className="member-image"
                        style={{
                          objectPosition: `${member.image_position_x || 50}% ${member.image_position_y || 50}%`
                        }}
                        onError={(e) => {
                          // If the resolved src still failed (e.g. member
                          // name not in fallback map), swap to the local
                          // asset by name once. Never retry the dead
                          // retro-band-cms host indefinitely.
                          const nameKey = (member.name || '').trim().toLowerCase();
                          const local = LOCAL_MEMBER_IMAGES[nameKey];
                          if (local && e.target.src.indexOf(local) === -1) {
                            e.target.src = local;
                          } else {
                            // Give up — hide the broken image; the
                            // .member-placeholder icon will show through.
                            e.target.style.display = 'none';
                          }
                        }}
                        onLoad={() => {
                          // Ensure image is visible once loaded
                          console.log(`Image loaded successfully: ${member.name}`);
                        }}
                      />
                    ) : (
                      <div className="member-placeholder">
                        {getInstrumentIcon(member.instrument)}
                      </div>
                    );
                    })()}
                  </div>
                  <div className="member-info">
                    <h3 className="member-name">{member.name}</h3>
                    <div className="member-role">
                      {getInstrumentIcon(member.instrument)}
                      <span>{member.instrument}</span>
                    </div>
                    <p className="member-bio">{member.bio}</p>
                    {member.favoriteArtist && (
                      <div className="member-favorite">
                        <strong>Favorite 50s Artist:</strong> {member.favoriteArtist}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="members-cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2 className="cta-title">Book the Rebel Angels</h2>
            <p className="cta-text">
              Ready to bring the magic of the 1950s to your event? Contact us for bookings!
            </p>
            <Button 
              className="cta-button"
              onClick={() => {
                console.log('Get in Touch button clicked - navigating to contact');
                // Navigate to home page first
                window.location.href = '/';
                // Set a flag to scroll to contact after page loads
                sessionStorage.setItem('scrollToContact', 'true');
              }}
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>&copy; 2025 Rebel Angels. All rights reserved.</p>
          <p className="footer-tagline">Keep the spirit of rock and roll alive</p>
        </div>
      </footer>
    </div>
  );
};

export default BandMembers;